import React, { useState, useEffect } from 'react';
import { getToken } from '../../utils/getToken';
import { NumberTo2DP } from '../../utils/NumberTo2DP';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://devapi.quickcare.ng/admin';

const FinanceStats = ({period, country}) => {
    const [stats, setStats] = useState({loading: true, data: {}});

    useEffect(() => {
        const fetchFinanceStats = async () => {
            const token = getToken();
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            if (token) {
                myHeaders.append("Authorization", `Bearer ${token}`);
            }

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };
            setStats(prev => ({...prev, loading: true}));
            try {
                const response = await fetch(`${baseURL}/financials/stats?start_date=${period.start_date}&end_date=${period.end_date}&country=${country}`, requestOptions);
                const result = await response.json();
                setStats({loading: false, data: result.data});
            } catch (error) {
                setStats({loading: false, data: {}});
                console.error('Error fetching financial stats:', error);
            }
        };

        fetchFinanceStats();
    }, [period, country]);

    const getPeriodLabel = () => {
        if (typeof period === 'string') {
            switch (period) {
                case '24hr':
                    return "Today's Revenue";
                case 'all':
                    return "All Revenue";
                case '1week':
                    return "This Week Revenue";
                case '2weeks':
                    return "2 Weeks Revenue";
                case '1month':
                    return "This Month Revenue";
                case '2months':
                    return "2 Months Revenue";
                case 'quarter':
                    return "Quarter Revenue";
                case '6months':
                    return "6 Months Revenue";
                case '1year':
                    return "1 Year Revenue";
                default:
                    return "Revenue";
            }
        }
        return "Revenue";
    };

    // Card configurations with icons and colors
    const cardConfigs = [
        {
            id: 1,
            title: getPeriodLabel(),
            value: stats?.data?.from_month_start_amount || 0,
            icon: '💰',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600',
            borderColor: 'border-blue-100',
            hoverColor: 'hover:border-blue-300',
            isAmount: true,
            subtitle: 'Revenue for selected period'
        },
        {
            id: 2,
            title: `${getPeriodLabel()} Count`,
            value: stats?.data?.from_month_start_count || 0,
            icon: '📊',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600',
            borderColor: 'border-purple-100',
            hoverColor: 'hover:border-purple-300',
            isAmount: false,
            subtitle: 'Transactions count'
        },
        {
            id: 3,
            title: 'Total Subscriptions',
            value: stats?.data?.total_subscriptions_amount || 0,
            icon: '📈',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600',
            borderColor: 'border-green-100',
            hoverColor: 'hover:border-green-300',
            isAmount: true,
            subtitle: 'Lifetime subscription revenue'
        },
        {
            id: 4,
            title: 'Total Count',
            value: stats?.data?.total_subscriptions_count || 0,
            icon: '👥',
            bgColor: 'bg-orange-50',
            textColor: 'text-orange-600',
            borderColor: 'border-orange-100',
            hoverColor: 'hover:border-orange-300',
            isAmount: false,
            subtitle: 'Total subscription count'
        },
        {
            id: 5,
            title: "Today's Amount",
            value: stats?.data?.today_subscription_amount || 0,
            icon: '⏰',
            bgColor: 'bg-red-50',
            textColor: 'text-red-600',
            borderColor: 'border-red-100',
            hoverColor: 'hover:border-red-300',
            isAmount: true,
            subtitle: 'Today\'s revenue'
        },
        {
            id: 6,
            title: "Today's Count",
            value: stats?.data?.today_subscription_count || 0,
            icon: '📅',
            bgColor: 'bg-indigo-50',
            textColor: 'text-indigo-600',
            borderColor: 'border-indigo-100',
            hoverColor: 'hover:border-indigo-300',
            isAmount: false,
            subtitle: 'Today\'s transactions'
        }
    ];

    return (
        <div className='w-full'>
            {stats && (
                <div className='mb-6'>
                    <div className='flex items-center gap-3 mb-4'>
                        <div className='w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full'></div>
                        <div>
                            <p className='text-xl font-bold text-gray-800'>Subscription Overview</p>
                            <p className='text-xs text-gray-400'>Real-time subscription metrics</p>
                        </div>
                        <span className='ml-auto text-xs bg-[#069125]/10 text-[#069125] px-3 py-1 rounded-full font-medium'>
                            {stats.loading ? '⏳ Loading...' : '✅ Live'}
                        </span>
                    </div>
                    
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4'>
                        {cardConfigs.map((card) => (
                            <div 
                                key={card.id}
                                className={`group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border ${card.borderColor} ${card.hoverColor} overflow-hidden transform hover:-translate-y-1`}
                            >
                                <div className='p-4 lg:p-6'>
                                    <div className='flex items-start justify-between mb-3'>
                                        <div className={`p-2.5 rounded-xl ${card.bgColor} group-hover:scale-110 transition-transform duration-300 text-2xl`}>
                                            {card.icon}
                                        </div>
                                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${card.bgColor} ${card.textColor}`}>
                                            {card.isAmount ? '💰 Amount' : '🔢 Count'}
                                        </span>
                                    </div>
                                    
                                    <h3 className='text-sm font-medium text-gray-600 mb-1'>
                                        {card.title}
                                    </h3>
                                    
                                    <p className='text-xl lg:text-2xl font-bold text-gray-800'>
                                        {stats.loading ? (
                                            <span className='inline-block w-24 h-7 bg-gray-200 animate-pulse rounded'></span>
                                        ) : (
                                            card.isAmount ? `$${NumberTo2DP(card.value)}` : card.value.toLocaleString()
                                        )}
                                    </p>
                                    
                                    <div className='mt-3 pt-3 border-t border-gray-100 flex items-center justify-between'>
                                        <span className='text-xs text-gray-400'>
                                            {card.subtitle}
                                        </span>
                                        {!stats.loading && (
                                            <span className='text-xs text-green-500 flex items-center gap-1'>
                                                <span className='inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse'></span>
                                                Live
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceStats;